import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { api, API } from "@/lib/api";
import { Share2, ChevronDown, MapPin, Trophy, Target, Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

function useCountUp(target, active, dur = 1400) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    const t = Number(target) || 0;
    if (t <= 0) { setN(0); return; }
    let raf, t0;
    const tick = (ts) => {
      if (!t0) t0 = ts;
      const p = Math.min(1, (ts - t0) / dur);
      setN(Math.round((1 - Math.pow(1 - p, 4)) * t));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, dur]);
  return n;
}

const rise = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

function Slide({ bg, children, testid }) {
  return (
    <section
      className={`h-screen snap-start flex flex-col items-center justify-center px-6 text-center relative overflow-hidden ${bg}`}
      data-testid={testid}>
      <motion.div initial="hidden" whileInView="show" viewport={{ once: false, amount: 0.6 }}
        variants={{ show: { transition: { staggerChildren: 0.15 } } }}
        className="max-w-2xl">
        {children}
      </motion.div>
      <ChevronDown className="absolute bottom-8 opacity-50 animate-bounce" size={28} />
    </section>
  );
}

export default function Retro() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [seen, setSeen] = useState({});
  const finishRef = useRef(null);

  useEffect(() => { api("/public/city-impact", { auth: false }).then(setData).catch(() => {}); }, []);

  useEffect(() => {
    // Fire confetti once the final slide becomes visible.
    if (!finishRef.current) return;
    const obs = new IntersectionObserver((e) => {
      if (e[0].isIntersecting) {
        confetti({ particleCount: 160, spread: 90, origin: { y: 0.4 },
          colors: ["#F5B301", "#191A54", "#0E9F6E", "#E4232E", "#fff"] });
      }
    }, { threshold: 0.6 });
    obs.observe(finishRef.current);
    return () => obs.disconnect();
  }, [data]);

  const totals = data?.totals || {};
  const yearly = data?.yearly || {};
  const year = yearly.year || new Date().getFullYear();
  const cities = data?.cities || [];
  const topCity = [...cities].filter((c) => c.total >= 2).sort((a, b) => b.rate - a.rate)[0];

  const restituted = useCountUp(totals.restituted || 0, !!data && seen.count);
  const rate = useCountUp(Math.round(totals.rate || 0), !!data && seen.rate);

  async function shareRecap() {
    const imgUrl = `${API}/public/year-recap-image.png?year=${year}`;
    const text = t("hc.findoReviewShare").replace("{year}", year).replace("{n}", totals.restituted || 0).replace("{rate}", totals.rate || 0);
    const url = window.location.origin;
    try {
      const res = await fetch(imgUrl);
      const blob = await res.blob();
      const file = new File([blob], `findloop-bilan-${year}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Findloop — ${t("hc.retro")} ${year}`, text, url }); return;
      }
      if (navigator.share) { await navigator.share({ title: `Findloop — ${t("hc.retro")} ${year}`, text, url }); return; }
      window.open(imgUrl, "_blank", "noopener");
    } catch (e) {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, "_blank", "noopener");
    }
  }

  const mark = (k) => setSeen((s) => (s[k] ? s : { ...s, [k]: true }));

  return (
    <div className="h-screen overflow-y-scroll snap-y snap-mandatory bg-findo-navy text-white" data-testid="retro-page">
      <Slide bg="bg-findo-navy" testid="retro-slide-intro">
        <motion.div variants={rise}><Sparkles className="mx-auto text-amber-300" size={44} /></motion.div>
        <motion.p variants={rise} className="mt-6 text-lg font-semibold uppercase tracking-widest text-amber-300">findloop</motion.p>
        <motion.h1 variants={rise} className="mt-3 font-display text-5xl md:text-7xl font-bold">{t("rt.yourRecap").replace("{year}", year)}</motion.h1>
        <motion.p variants={rise} className="mt-5 text-white/70 text-lg">{t("pub.retroScroll")}</motion.p>
      </Slide>

      <motion.section
        onViewportEnter={() => mark("count")}
        className="h-screen snap-start flex flex-col items-center justify-center px-6 text-center bg-findo-red relative"
        data-testid="retro-slide-count">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: false, amount: 0.6 }}
          variants={{ show: { transition: { staggerChildren: 0.15 } } }} className="max-w-2xl">
          <motion.p variants={rise} className="text-lg font-semibold uppercase tracking-widest text-white/80">{t("pub.thisYear")}</motion.p>
          <motion.div variants={rise} className="mt-3 font-display text-7xl md:text-9xl font-extrabold text-white">{restituted}</motion.div>
          <motion.p variants={rise} className="mt-3 text-2xl font-semibold">{t("pub.itemsReturnedOwners")}</motion.p>
        </motion.div>
        <ChevronDown className="absolute bottom-8 opacity-50 animate-bounce" size={28} />
      </motion.section>

      <motion.section
        onViewportEnter={() => mark("rate")}
        className="h-screen snap-start flex flex-col items-center justify-center px-6 text-center bg-emerald-600 relative"
        data-testid="retro-slide-rate">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: false, amount: 0.6 }}
          variants={{ show: { transition: { staggerChildren: 0.15 } } }} className="max-w-2xl">
          <motion.p variants={rise} className="text-lg font-semibold uppercase tracking-widest text-white/80">{t("rt.restitutionRate")}</motion.p>
          <motion.div variants={rise} className="mt-3 font-display text-7xl md:text-9xl font-extrabold">{rate}%</motion.div>
          <motion.p variants={rise} className="mt-3 text-xl text-white/90">{t("pub.itemsFoundOwner")}</motion.p>
        </motion.div>
        <ChevronDown className="absolute bottom-8 opacity-50 animate-bounce" size={28} />
      </motion.section>

      {topCity && (
        <Slide bg="bg-[#3D3AB8]" testid="retro-slide-city">
          <motion.div variants={rise}><Trophy className="mx-auto text-amber-300" size={44} /></motion.div>
          <motion.p variants={rise} className="mt-5 text-lg font-semibold uppercase tracking-widest text-white/80">{t("rt.championCity")}</motion.p>
          <motion.h2 variants={rise} className="mt-3 font-display text-6xl md:text-8xl font-bold flex items-center justify-center gap-3">
            <MapPin className="text-amber-300" size={44} /> {topCity.city}
          </motion.h2>
          <motion.p variants={rise} className="mt-4 text-2xl font-semibold text-amber-300">{t("rt.cityRate").replace("{rate}", topCity.rate)}</motion.p>
        </Slide>
      )}

      {yearly.target > 0 && (
        <Slide bg="bg-findo-navy" testid="retro-slide-objective">
          <motion.div variants={rise}><Target className="mx-auto text-amber-300" size={44} /></motion.div>
          <motion.p variants={rise} className="mt-5 text-lg font-semibold uppercase tracking-widest text-white/80">{t("hc.annualTarget")}</motion.p>
          <motion.h2 variants={rise} className="mt-3 font-display text-6xl md:text-8xl font-bold">
            {yearly.rate >= yearly.target ? t("hc.reached") : `${yearly.progress}%`}
          </motion.h2>
          <motion.p variants={rise} className="mt-4 text-xl text-white/80">
            {t("hc.rateOfTarget").replace("{rate}", yearly.rate).replace("{target}", yearly.target)}
          </motion.p>
        </Slide>
      )}

      <section ref={finishRef}
        className="h-screen snap-start flex flex-col items-center justify-center px-6 text-center bg-gradient-to-b from-findo-navy to-[#3D3AB8] relative"
        data-testid="retro-slide-finish">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: false, amount: 0.6 }}
          variants={{ show: { transition: { staggerChildren: 0.15 } } }} className="max-w-2xl">
          <motion.h2 variants={rise} className="font-display text-5xl md:text-7xl font-bold">{t("rt.thanks").replace("{year}", year)}</motion.h2>
          <motion.p variants={rise} className="mt-5 text-white/70 text-lg">{t("pub.retroClosing")}</motion.p>
          <motion.div variants={rise} className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={shareRecap} data-testid="retro-share-btn"
              className="inline-flex items-center gap-2 rounded-full bg-white text-findo-navy text-sm font-bold px-6 py-3 hover:bg-white/90 transition-colors">
              <Share2 size={16} /> {t("rt.shareRecap")}
            </button>
            <Link to="/" className="inline-flex items-center rounded-full border border-white/40 text-white text-sm font-semibold px-6 py-3 hover:bg-white/10 transition-colors" data-testid="retro-home-link">
              {t("hc.backToHome")}
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}
